import { useEffect, useRef } from 'react';
import { Star } from 'lucide-react';
import caIndia from '../assets/ca-india.png';
import icaew from '../assets/icaew.png';
import taxCloud from '../assets/tax-cloud.png';
import tallyPartner from '../assets/tally-partner.png';
import zohoBooks from '../assets/zoho-books.png';
import quickBooks from '../assets/quickbooks.png';

const partners = [
	{
		logo: caIndia,
		name: 'CA INDIA',
		description: 'Institute of Chartered Accountants of India',
		badge: 'ICAI Member',
	},
	{
		logo: icaew,
		name: 'ICAEW',
		description: 'Institute of Chartered Accountants in England & Wales',
		badge: 'ACA Member',
	},
	{
		logo: taxCloud,
		name: 'TaxCloud',
		description: 'Advanced Tax Filing & Compliance Platform',
		badge: 'Certified Partner',
	},
	{
		logo: tallyPartner,
		name: 'Tally Partner',
		description: 'Tally Solutions - Accounting Software',
		badge: '3-Star Certified',
		stars: 3,
	},
	{
		logo: zohoBooks,
		name: 'Zoho Books',
		description: 'Zoho Corporation - Cloud Accounting',
		badge: 'Certified Associate',
	},
	{
		logo: quickBooks,
		name: 'QuickBooks',
		description: 'Intuit QuickBooks - Financial Management',
		badge: 'ProAdvisor Certified',
	},
];

const CertifiedPartners = () => {
	const sectionRef = useRef(null);

	useEffect(() => {
		const cards = sectionRef.current?.querySelectorAll('.partner-card');
		if (!cards?.length) return;

		cards.forEach((card) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(28px)';
			card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
		});

		const io = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					cards.forEach((card, i) => {
						setTimeout(() => {
							card.style.opacity = '1';
							card.style.transform = 'translateY(0)';
						}, i * 90);
					});
					io.disconnect();
				}
			},
			{ threshold: 0.05, rootMargin: '0px 0px -40px 0px' },
		);

		if (sectionRef.current) io.observe(sectionRef.current);
		return () => io.disconnect();
	}, []);

	return (
		<section
			ref={sectionRef}
			data-testid='certified-partners-section'
			id='certifications'
			aria-label='Professional certifications and partners'
			className='py-20'
			style={{ backgroundColor: '#1B8A96' }}>
			<div className='max-w-6xl mx-auto px-6'>
				<div className='text-center mb-14'>
					<p
						className='text-xs font-semibold uppercase tracking-widest mb-3 opacity-80'
						style={{ color: '#ffffff' }}>
						Credentials & Trust
					</p>
					<h2
						className='text-3xl sm:text-4xl font-bold mb-4 text-white'
						style={{ fontFamily: "'Outfit', sans-serif" }}>
						Certified Partner
					</h2>
					<p
						className='text-base max-w-xl mx-auto opacity-85'
						style={{ color: '#E0F4F5' }}>
						With our expertise in the domain and certifications from top
						industry tools, we deliver comprehensive consultancy solutions.
					</p>
				</div>

				<div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'>
					{partners.map(({ logo, name, description, badge, stars }, i) => (
						<div
							key={i}
							data-testid={`partner-card-${i}`}
							className='partner-card rounded-2xl p-4 text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-default flex flex-col items-center gap-2'
							style={{
								backgroundColor: 'white',
								boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
							}}>
							<div className='w-24 h-24 flex items-center justify-center mb-1'>
								<img
									src={logo}
									alt={`${name} certified partner`}
									className='max-w-full max-h-full object-contain'
									loading='lazy'
									decoding='async'
								/>
							</div>
							<div
								className='font-bold text-sm leading-tight'
								style={{ color: '#1F2937' }}>
								{name}
							</div>
							{stars && (
								<div className='flex gap-0.5'>
									{Array.from({ length: stars }).map((_, si) => (
										<Star
											key={si}
											size={10}
											fill='#F59E0B'
											style={{ color: '#F59E0B' }}
										/>
									))}
								</div>
							)}
							<span
								className='text-xs px-2 py-0.5 rounded-full font-medium'
								style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
								{badge}
							</span>
							<p
								className='text-xs leading-tight opacity-70 hidden lg:block'
								style={{ color: '#6B7280' }}>
								{description}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default CertifiedPartners;
